import { useState } from 'react'
import { Cpu } from 'lucide-react'
import { toast } from 'sonner'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useInitFromServer } from '@/hooks/useInitFromServer'
import {
  DutyCard,
  DutyCardBody,
  DutyCardContent,
  DutyCardEditFooter,
  DutyCardHeader,
} from '@/features/duties/components/cards/shared/DutyCard'
import type { EvalCaseDetail } from '../../types'
import { useUpdateEvalCaseMutation } from '../../hooks/useUpdateEvalCaseMutation'

export function EvalModelCard({ evalCase }: { evalCase: EvalCaseDetail }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [model, setModel] = useState(evalCase.model)
  const [provider, setProvider] = useState(evalCase.provider)
  const [dutyModel, setDutyModel] = useState(evalCase.duty_model ?? '')
  const [dutyProvider, setDutyProvider] = useState(evalCase.duty_provider ?? '')
  const updateCase = useUpdateEvalCaseMutation(evalCase.id)

  useInitFromServer(evalCase.model, setModel)
  useInitFromServer(evalCase.provider, setProvider)
  useInitFromServer(evalCase.duty_model ?? '', setDutyModel)
  useInitFromServer(evalCase.duty_provider ?? '', setDutyProvider)

  // TODO(routing PR 6 follow-up): register this card with
  // `useRegisterTabEditor` (hooks/useRegisterTabEditor) so the
  // close dialog can save it. Pattern: EvalPromptCard.
  const isDirty =
    model !== evalCase.model ||
    provider !== evalCase.provider ||
    dutyModel !== (evalCase.duty_model ?? '') ||
    dutyProvider !== (evalCase.duty_provider ?? '')

  const handleSave = () => {
    updateCase.mutate(
      {
        model: model.trim(),
        provider: provider.trim(),
        duty_model: dutyModel.trim() || null,
        duty_provider: dutyProvider.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success('Model saved')
          setIsExpanded(false)
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to save model')
        },
      }
    )
  }

  const handleCancel = () => {
    setModel(evalCase.model)
    setProvider(evalCase.provider)
    setDutyModel(evalCase.duty_model ?? '')
    setDutyProvider(evalCase.duty_provider ?? '')
  }

  return (
    <DutyCard>
      <DutyCardHeader
        icon={<Cpu />}
        title="Models"
        expanded={isExpanded}
        onToggleExpanded={() => setIsExpanded((prev) => !prev)}
        expandLabel="Edit models"
        collapseLabel="Collapse models"
      />
      <DutyCardBody>
        <DutyCardContent>
          {isExpanded ? (
            <div className="flex flex-col gap-3">
              <p className="text-[11.5px] font-semibold uppercase tracking-wide text-neutral-low-contrast">
                Counterparty agent
              </p>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="counterparty-model" className="text-xs">
                  Model
                </Label>
                <Input
                  id="counterparty-model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="e.g. gpt-4o-mini"
                  disabled={updateCase.isPending}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="counterparty-provider" className="text-xs">
                  Provider
                </Label>
                <Input
                  id="counterparty-provider"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  placeholder="e.g. openai"
                  disabled={updateCase.isPending}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="duty-model" className="text-xs">
                  Duty agent model{' '}
                  <span className="text-neutral-low-contrast">(optional)</span>
                </Label>
                <Input
                  id="duty-model"
                  value={dutyModel}
                  onChange={(e) => setDutyModel(e.target.value)}
                  placeholder="Leave blank to use duty default"
                  disabled={updateCase.isPending}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="duty-provider" className="text-xs">
                  Duty agent provider{' '}
                  <span className="text-neutral-low-contrast">(optional)</span>
                </Label>
                <Input
                  id="duty-provider"
                  value={dutyProvider}
                  onChange={(e) => setDutyProvider(e.target.value)}
                  placeholder="Leave blank to use duty default"
                  disabled={updateCase.isPending}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div>
                <p className="m-0 text-[11.5px] font-medium text-neutral-low-contrast">
                  Counterparty
                </p>
                <p className="m-0 text-[13px] text-primary-high-contrast">
                  {evalCase.model}
                  <span className="text-neutral-low-contrast">
                    {' '}
                    · {evalCase.provider}
                  </span>
                </p>
              </div>
              <div>
                <p className="m-0 text-[11.5px] font-medium text-neutral-low-contrast">
                  Duty agent
                </p>
                <p className="m-0 text-[13px] text-primary-high-contrast">
                  {evalCase.duty_model ? (
                    <>
                      {evalCase.duty_model}
                      {evalCase.duty_provider ? (
                        <span className="text-neutral-low-contrast">
                          {' '}
                          · {evalCase.duty_provider}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-neutral-low-contrast">
                      Duty default
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}
        </DutyCardContent>
        {isExpanded ? (
          <DutyCardEditFooter
            onCancel={handleCancel}
            onSave={handleSave}
            onCollapse={() => setIsExpanded(false)}
            isDirty={isDirty}
            isSaving={updateCase.isPending}
          />
        ) : null}
      </DutyCardBody>
    </DutyCard>
  )
}
